/**
 * @file featureRoute.js
 * @author Simon Tenedero
 * @created 2025-07-09
 * @lastModified 2025-07-09
 * @description Route guard component that protects pages from users who do not have a certain feature
 */

import { Navigate } from "react-router-dom";
import { useFeatureFlags } from "../context/FeatureContext";

/**
 * Route guard component that only allows users with a specific feature flag to access protected routes
 * @param {Object} props - Component props
 * @param {string} props.featureFlag - The feature flag to check against the user's permissions
 * @param {ReactNode} props.children - Components to render if user has the feature flag
 * @param {string} [props.redirectTo='/'] - Path to redirect users who do not have the feature flag
 *
 * @returns {JSX.Element} Children if user has feature flag, loading spinner if checking permissions,
 * or redirect to specified path if user does not have the feature flag
 */
function FeatureRoute({ featureFlag, children, redirectTo = "/" }) {
  const { hasFeature, loading } = useFeatureFlags();

  // Show loading while checking permissions
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-t from-base-300 via-base-200 to-base-100">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Redirect if user is not admin (in case they typed URL directly)
  if (!hasFeature(featureFlag)) {
    console.warn(
      `Access denied to this route. Your role is missing the proper permissions.`,
    );
    return <Navigate to={redirectTo} replace />;
  }

  // User is admin, render the protected component
  return children;
}

export default FeatureRoute;
